import { useDeferredValue, useMemo, useState } from "react";
import type { ObjectDirectoryEntry, OrgScanScope } from "@ai-sales/contracts";

const operationLabels: Partial<Record<OrgScanScope["operations"][number], string>> = {
  create: "创建",
  update: "更新",
  delete: "删除（仅单条）"
};

type SelectionMap = Record<string, OrgScanScope["operations"]>;

function availableOperations(entry: ObjectDirectoryEntry): OrgScanScope["operations"] {
  return [
    ...(entry.queryable ? ["query", "aggregate"] as const : []),
    ...(entry.createable ? ["create"] as const : []),
    ...(entry.updateable ? ["update"] as const : []),
    ...(entry.deletable ? ["delete"] as const : [])
  ];
}

export function OrgObjectSelector(props: {
  directory: ObjectDirectoryEntry[];
  selections: SelectionMap;
  maxSelections: number;
  disabled?: boolean;
  onChange: (next: SelectionMap) => void;
}) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<"all" | "standard" | "custom">("all");
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const entriesByName = useMemo(
    () => new Map(props.directory.map((entry) => [entry.objectApiName, entry])),
    [props.directory]
  );
  const selectedNames = useMemo(
    () => Object.keys(props.selections).filter((name) => props.selections[name]?.length),
    [props.selections]
  );
  const available = useMemo(() => props.directory
    .filter((entry) => !selectedNames.includes(entry.objectApiName))
    .filter((entry) => kind === "all" || (kind === "custom" ? entry.custom : !entry.custom))
    .filter((entry) => !deferredQuery || `${entry.label}${entry.labelPlural}${entry.objectApiName}`.toLowerCase().includes(deferredQuery))
    .sort((left, right) => left.label.localeCompare(right.label, "zh-CN")),
  [deferredQuery, kind, props.directory, selectedNames]);
  const selected = useMemo(() => selectedNames
    .map((name) => entriesByName.get(name))
    .filter((entry): entry is ObjectDirectoryEntry => Boolean(entry)),
  [entriesByName, selectedNames]);
  const atLimit = selected.length >= props.maxSelections;

  const add = (entry: ObjectDirectoryEntry) => {
    if (props.disabled || atLimit) return;
    if (!entry.queryable) return;
    props.onChange({ ...props.selections, [entry.objectApiName]: ["query", "aggregate"] });
  };
  const remove = (objectApiName: string) => {
    if (props.disabled) return;
    const next = { ...props.selections };
    delete next[objectApiName];
    props.onChange(next);
  };
  const toggleOperation = (
    entry: ObjectDirectoryEntry,
    operation: OrgScanScope["operations"][number],
    checked: boolean
  ) => {
    const current = props.selections[entry.objectApiName] ?? [];
    const operations = checked
      ? [...new Set([...current, operation])]
      : current.filter((item) => item !== operation);
    if (!operations.length) {
      remove(entry.objectApiName);
      return;
    }
    props.onChange({ ...props.selections, [entry.objectApiName]: operations });
  };
  const toggleRead = (entry: ObjectDirectoryEntry, checked: boolean) => {
    const current = props.selections[entry.objectApiName] ?? [];
    const withoutRead = current.filter((operation) => operation !== "query" && operation !== "aggregate");
    const operations = checked ? (["query", "aggregate", ...withoutRead] as OrgScanScope["operations"]) : withoutRead;
    if (!operations.length) return remove(entry.objectApiName);
    props.onChange({ ...props.selections, [entry.objectApiName]: operations });
  };

  return <section className="object-selector" aria-label="Salesforce 对象扫描范围">
    <header className="object-selector__toolbar">
      <label>搜索对象
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="输入对象名称或 API 名称"
        />
      </label>
      <label>对象类型
        <select value={kind} onChange={(event) => setKind(event.target.value as typeof kind)}>
          <option value="all">全部对象</option>
          <option value="standard">标准对象</option>
          <option value="custom">自定义对象</option>
        </select>
      </label>
      <span className={atLimit ? "object-selector__limit object-selector__limit--full" : "object-selector__limit"}>
        已选 {selected.length}/{props.maxSelections}
      </span>
    </header>
    <div className="object-selector__columns">
      <section className="object-selector__column" aria-labelledby="available-objects-heading">
        <header><div><h3 id="available-objects-heading">可选对象</h3><p>从当前 Org 读取，仅显示可用于 API 的对象。</p></div><span>{available.length}</span></header>
        <div className="object-selector__list">
          {available.map((entry) => <article className="object-selector__row" key={entry.objectApiName}>
            <div><strong>{entry.label}</strong><small>{entry.objectApiName}{entry.custom ? " · 自定义" : ""}</small></div>
            <button
              type="button"
              className="object-selector__move"
              aria-label={`添加 ${entry.label}`}
              disabled={props.disabled || atLimit || !entry.queryable}
              onClick={() => add(entry)}
            >添加 →</button>
          </article>)}
          {!available.length ? <p className="object-selector__empty">没有匹配的可选对象。</p> : null}
        </div>
      </section>
      <section className="object-selector__column object-selector__column--selected" aria-labelledby="selected-objects-heading">
        <header><div><h3 id="selected-objects-heading">已选对象</h3><p>仅这些对象会被扫描并进入 AI 能力边界。</p></div><span>{selected.length}</span></header>
        <div className="object-selector__list">
          {selected.map((entry) => {
            const operations = availableOperations(entry);
            const current = props.selections[entry.objectApiName] ?? [];
            return <article className="object-selector__selected-row" key={entry.objectApiName}>
              <div className="object-selector__selected-title"><div><strong>{entry.label}</strong><small>{entry.objectApiName}{entry.custom ? " · 自定义" : ""}</small></div><button type="button" className="text-button" disabled={props.disabled} aria-label={`移除 ${entry.label}`} onClick={() => remove(entry.objectApiName)}>移除</button></div>
              <fieldset><legend>基础操作</legend>
                {entry.queryable ? <label><input type="checkbox" disabled={props.disabled} checked={current.includes("query") && current.includes("aggregate")} onChange={(event) => toggleRead(entry, event.target.checked)} />读取（查询、筛选、排序和统计）</label> : null}
                {operations.filter((operation) => operation !== "query" && operation !== "aggregate").map((operation) => <label key={operation}><input type="checkbox" disabled={props.disabled} checked={current.includes(operation)} onChange={(event) => toggleOperation(entry, operation, event.target.checked)} />{operationLabels[operation]}</label>)}
              </fieldset>
            </article>;
          })}
          {!selected.length ? <p className="object-selector__empty">扫描范围当前为空。请从左侧添加需要由 AI 助手访问的对象。</p> : null}
        </div>
      </section>
    </div>
  </section>;
}
